import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { consumeRedirectAfterAuth } from "../../utils/redirectAfterAuth";
import "./finishRegistration.css";

const MARKS = [18, 40, 62, 82, 97];

export default function FinishRegistration() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const STEP_LABELS = [
    t("auth.finishStep1"),
    t("auth.finishStep2"),
    t("auth.finishStep3"),
    t("auth.finishStep4"),
    t("auth.finishStep5"),
  ];

  const SUBS = [
    t("auth.finishSub1"),
    t("auth.finishSub2"),
    t("auth.finishSub3"),
    t("auth.finishSub4"),
    t("auth.finishSub5"),
  ];

  const DONE_SUB = t("auth.finishDoneSub");
  const stageRef = useRef(null);
  const ringRef = useRef(null);
  const pctNumRef = useRef(null);
  const subtitleRef = useRef(null);
  const stepsRef = useRef(null);

  const doNavigate = () => {
    try {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("token");
      if (!token) {
        navigate("/sign-in", { replace: true });
        return;
      }
      localStorage.setItem("accessToken", token);
      navigate(consumeRedirectAfterAuth() || "/profile", { replace: true });
    } catch (err) {
      console.error("❌ Redirect failed:", err);
      navigate("/sign-in", { replace: true });
    }
  };

  useEffect(() => {
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const stage = stageRef.current;
    const ring = ringRef.current;
    const pctNum = pctNumRef.current;
    const subtitle = subtitleRef.current;
    const steps = Array.from(stepsRef.current.querySelectorAll(".fb-step"));

    const timers = [];
    const at = (fn, ms) => timers.push(setTimeout(fn, ms));
    const clearAll = () => timers.forEach(clearTimeout);

    // Floating sparks
    const sparks = [];
    for (let i = 0; i < 14; i++) {
      const s = document.createElement("span");
      s.className = "fb-spark";
      s.style.left = 10 + Math.random() * 80 + "%";
      s.style.top = 15 + Math.random() * 70 + "%";
      const dur = (2 + Math.random() * 3).toFixed(2);
      const delay = (Math.random() * 4).toFixed(2);
      s.style.animation = `fb-floaty ${dur}s ease-in-out ${delay}s infinite`;
      stage.appendChild(s);
      sparks.push(s);
    }

    function reset() {
      clearAll();
      stage.classList.remove("complete");
      ring.style.setProperty("--p", 0);
      pctNum.textContent = "0";
      subtitle.textContent = SUBS[0];
      subtitle.style.opacity = 1;
      steps.forEach((st) => st.classList.remove("active", "done", "pending"));
    }

    function setSub(txt) {
      subtitle.style.opacity = 0;
      at(() => {
        subtitle.textContent = txt;
        subtitle.style.opacity = 1;
      }, 200);
    }

    let rafId = null;

    function finish() {
      steps.forEach((st) => {
        st.classList.remove("active");
        st.classList.add("done");
      });
      setSub(DONE_SUB);
      at(() => stage.classList.add("complete"), 350);
      at(doNavigate, 2300);
    }

    function run() {
      reset();
      const dur = 6000 + Math.round(Math.random() * 4000);
      const t0 = performance.now();
      let curStep = -1;

      at(() => {
        steps[0].classList.add("active");
        curStep = 0;
      }, 250);

      function frame(now) {
        const p = Math.min((now - t0) / dur, 1);
        const eased = p < 1 ? 1 - Math.pow(1 - p, 2.2) : 1;
        const val = Math.round(eased * 100);

        ring.style.setProperty("--p", val);
        pctNum.textContent = val;

        let reached = 0;
        for (let i = 0; i < MARKS.length; i++) {
          if (val >= MARKS[i]) reached = i + 1;
        }

        if (reached - 1 > curStep) {
          for (let j = Math.max(curStep, 0); j < reached; j++) {
            if (steps[j]) {
              steps[j].classList.remove("active");
              steps[j].classList.add("done");
            }
          }
          curStep = reached - 1;
          if (steps[reached] && reached < steps.length) {
            steps[reached].classList.add("active");
          }
          if (SUBS[reached] && reached < SUBS.length) {
            setSub(SUBS[reached]);
          }
        }

        if (p < 1) {
          rafId = requestAnimationFrame(frame);
        } else {
          finish();
        }
      }

      rafId = requestAnimationFrame(frame);
    }

    if (reduce) {
      ring.style.setProperty("--p", 100);
      pctNum.textContent = "100";
      steps.forEach((st) => st.classList.add("done"));
      subtitle.textContent = DONE_SUB;
      stage.classList.add("complete");
      at(doNavigate, 2000);
    } else {
      run();
    }

    return () => {
      clearAll();
      if (rafId) cancelAnimationFrame(rafId);
      sparks.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  return (
    <div className="fb-stage" ref={stageRef}>
      {/* Progress orb */}
      <div className="fb-orb">
        <div className="fb-ring" ref={ringRef} />
        <div className="fb-sweep" />
        <div className="fb-core">
          <div className="fb-pct">
            <span ref={pctNumRef}>0</span>
            <span className="fb-u">%</span>
          </div>
          <div className="fb-core-label">{t("auth.finishCoreLabel")}</div>
        </div>
        <div className="fb-done-check">
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12.5l5 5L20 6.5" />
          </svg>
        </div>
      </div>

      {/* Headline */}
      <div className="fb-title">
        {t("auth.finishTitlePrefix")}{" "}
        <span className="fb-nb">
          <span className="fb-hl">Halper</span> AI
        </span>
      </div>
      <div className="fb-subtitle" ref={subtitleRef}>
        {t("auth.finishInitialSubtitle")}
      </div>

      {/* Status steps */}
      <div className="fb-steps" ref={stepsRef}>
        {STEP_LABELS.map((label, i) => (
          <div className="fb-step" key={i} data-step={i}>
            <span className="fb-ic">
              <span className="fb-spinner" />
              <span className="fb-dot" />
              <span className="fb-check">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <path d="M4 12l5 5L20 6" />
                </svg>
              </span>
            </span>
            <span className="fb-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
